<template>
  <el-tabs class="channel-menu" v-model="activeName">
    <el-tab-pane
      v-for="channel in filteredChannels"
      :key="channel"
      :label="$t(channels[channel])"
      class="preview"
      :name="channel"
    >
      <div
        class="full-size"
        v-if="activeName === channel"
        :data-qa="`preview-page-${channel}`"
      >
        <component
          :is="previewComponents[channel] || null"
          :key="deviceKey"
          :model="model"
          :variant-key="variantKey"
          :workflow-action="workflowAction"
          :profile-id="profileId"
          :selected-profile="selectedProfile"
          :custom-attributes="customAttributes"
          :language="getLanguage"
          :push-platform="pushPlatform"
          @push-platform-change="pushPlatform = $event"
        />
      </div>
    </el-tab-pane>
  </el-tabs>
</template>

<script>
import PreviewContentEmail from '@/modules/campaign-manage/components/preview/PreviewContentEmail.vue'
import PreviewContentPush from '@/modules/campaign-manage/components/preview/PreviewContentPush.vue'
import PreviewContentInApp from '@/modules/campaign-manage/components/preview/PreviewContentInApp.vue'
import PreviewContentInbox from '@/modules/campaign-manage/components/preview/PreviewContentInbox.vue'
import { channelLabels } from '@/modules/campaign-manage/enum/Labels'
import { campaignTypeEnum } from '@/modules/campaigns/enum/CampaignTypeEnum'

export default {
  name: 'PreviewContent',
  components: {
    PreviewContentEmail,
    PreviewContentPush,
    PreviewContentInApp,
    PreviewContentInbox
  },
  props: {
    model: {
      type: Object,
      required: true
    },
    variantKey: {
      type: String,
      required: true
    },
    workflowAction: {
      type: Object,
      required: false,
      default: () => ({})
    },
    customAttributes: {
      type: Object,
      required: false
    },
    language: {
      type: String,
      required: false
    },
    profileId: {
      type: String,
      required: false,
      default: null
    },
    /**
     * Full profile object (id, email, user_id, customer_id, …) corresponding to
     * `profileId`. Threaded through to the channel preview components so the
     * test-send dialogs can show a human label for the selected profile in their
     * description without re-fetching it.
     */
    selectedProfile: {
      type: Object,
      required: false,
      default: null
    }
  },
  data() {
    return {
      activeName: '',
      channelLabels,
      deviceKey: 0,
      pushPlatform: null
    }
  },
  computed: {
    previewComponents() {
      return {
        push: PreviewContentPush,
        inapp: PreviewContentInApp,
        inbox: PreviewContentInbox,
        email: PreviewContentEmail
      }
    },
    supportedChannels() {
      const channels = ['email']
      if (this.$hasFeature('extendedContentPreview')) {
        channels.push('push', 'inapp', 'inbox')
      }
      return channels
    },
    filteredChannels() {
      return this.selectedChannels.filter(channel =>
        this.supportedChannels.includes(channel)
      )
    },
    channels() {
      return {
        ...this.channelLabels,
        push: 'CampaignForm_ChannelsSelect_MobilePush',
        inapp: 'CampaignForm_ChannelsSelect_Inapp'
      }
    },
    selectedChannels() {
      if (this.model.type === campaignTypeEnum.TYPE_WORKFLOW) {
        // Hide inapp preview tab from JB 1.0 campaigns
        return this.model.new_mxa
          ? ['push', 'inapp', 'inbox', 'email']
          : ['push', 'inbox', 'email']
      }

      const hasPush = this.model.ios_push === 1 || this.model.android_push === 1
      const orderedChannels = ['push', 'inapp', 'inbox', 'email']
      const result = orderedChannels.filter(channel => {
        if (channel === 'push') return hasPush
        return this.model[channel] === 1
      })

      const otherEnabled = Object.keys(this.channels).filter(
        channel =>
          this.model[channel] === 1 &&
          channel !== 'ios_push' &&
          channel !== 'android_push' &&
          !orderedChannels.includes(channel)
      )

      return [...result, ...otherEnabled]
    },

    getLanguage() {
      return this.model.languages_multi ? this.language : ''
    }
  },

  methods: {
    reloadData() {
      this.deviceKey++
    }
  },
  watch: {
    activeName: {
      immediate: true,
      handler(channel) {
        this.$emit('channel-change', channel)
      }
    },
    filteredChannels: {
      immediate: true,
      handler(channels) {
        if (channels.length && !channels.includes(this.activeName)) {
          this.activeName = channels[0]
        }
      }
    },
    variantKey() {
      this.reloadData()
    },
    language() {
      this.reloadData()
    },
    customAttributes: {
      handler() {
        this.reloadData()
      },
      deep: true
    },
    profileId() {
      this.reloadData()
    },
    workflowAction: {
      handler(newVal) {
        if (newVal && newVal.platform) {
          this.pushPlatform = newVal.platform
        }
        this.reloadData()
      },
      deep: true
    }
  }
}
</script>

<style scoped>
.channel-menu {
  width: 95%;
}

>>> .el-tabs__header {
  padding-left: 30px;
  padding-top: 22px;
  margin: 0 0 0;
  background-color: #ffffff;
}

>>> .el-tabs__nav-wrap {
  margin-top: -1px;
}

>>> .el-tabs__nav-wrap::after {
  background-color: #ffffff;
}

.preview {
  background-color: #f8fafc;
}

.full-size {
  display: flex;
  flex-direction: column;
  width: 96%;
  margin-left: 2%;
  margin-bottom: 70px;
}

.device-selector {
  margin-bottom: 20px;
  padding-left: 30px;
  padding-top: 30px;
}
</style>



// WEBPACK FOOTER //
// src/modules/campaign-manage/components/preview/PreviewContent.vue